import React, {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {FormikProps, useFormik} from "formik";
import * as Yup from "yup";
import {useIntl} from "react-intl";
import md5 from "md5";
import {Button, FormInput, Forms, notify, VerificationPinModal} from "@housebets/housebets-x-package";

import {useAppSelector} from "hooks/useAppSelector";
import apiUser from "app/api/user";

import {IChangePasswordFormProps, TFormikConfig, TPayload} from "./types";
import {loginOut} from "app/store/slices/user";
import {PinForm} from "app/components/features/forms";
import {getPasswordSchema} from "app/utils/schemaValidation";
import { useDispatch } from "react-redux";

const initData: TFormikConfig = {
    newPassword: "",
    confirmPassword: "",
};

const initForm = (formik: FormikProps<TFormikConfig>, setError?: (v: boolean) => void) => {
    formik.setValues({...initData});
    formik.setErrors({});
    formik.setTouched({});
    setError?.(false);
};
export const ChangePasswordForm: React.FC<IChangePasswordFormProps> = ({onSubmit = () => {}}) => {
    const {$t} = useIntl();
    const navigate = useNavigate();
    const email = useAppSelector((state) => state.user.user?.email) || "";
    const dispatch = useDispatch();

    const formSchema = () =>
        Yup.object().shape({
            newPassword: getPasswordSchema($t),
            confirmPassword: Yup.string()
                .required($t({id: 'FieldIsRequired'}))
                .oneOf([Yup.ref("newPassword")], $t({id: "passwordMatch"})),
        });

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [isverifyIng, setIsverifyIng] = useState(false);

    const sendVerifyCode = async () => {
        setIsLoading(true);
        try {
            const {status, data} = await apiUser.getVerificationCode("email", email, "CHANGE_PASSWORD");
            if (status === 200 && data.code === 0) {
                setIsModalOpen(true);
                notify({
                    type: "success",
                    title: $t({id: "CodeSendSuccess"}),
                });
                return;
            }
            notify({
                type: "error",
                title: $t({id: `VerifyPhoneFail${data.code}`}),
            });
        } finally {
            setIsLoading(false);
        }
    };

    const submitVerifyCode = async (pin: string | undefined) => {
        try {
            setIsverifyIng(true);
            const res = await apiUser.modifyUserPassword(String(pin), md5(newPassword));

            if (res.status === 200 && res.data.code === 0) {
                setIsModalOpen(false);
                initForm(formik);
                onSubmit();
                // dispatch(loginOut());
                notify({
                    type: "success",
                    title: $t({id: "SuccessChangePassword"}),
                });
                // setTimeout(() => {
                //     navigate("/");
                // }, 10);
                return;
            }

            notify({
                type: "error",
                title: $t({id: `forgetPwCode_${res.data.code}`}),
            });

            setError($t({id: `forgetPwCode_${res.data.code}`}));
        } finally {
            setIsverifyIng(false);
        }
    };

    const formik: FormikProps<TFormikConfig> = useFormik<TFormikConfig>({
        initialValues: {...initData},
        validationSchema: formSchema(),
        validateOnBlur: true,
        onSubmit: async (data) => {
            setNewPassword(data.newPassword);
            sendVerifyCode();
        },
    });

    const config = useMemo(() => {
        return {
            title: $t({id: "ChangePasswordForm.codeSentTitle"}),
            value: email,
            submitButtonText: $t({id: "ChangePasswordForm.confirmCode"}),
            requestCodeButtonText: $t({id: "ChangePasswordForm.requestCodeAgain"}),
            isLoading: isverifyIng,
        };
    }, [email, isverifyIng]);

    useEffect(() => {
        initForm(formik);
    }, []);

    return (
        <>
            <Forms.ChangePass.Wrapper>
                <Forms.ChangePass.Form onSubmit={formik.handleSubmit}>
                    <Forms.ChangePass.Row>
                        <FormInput
                            showIcon={false}
                            size="sm"
                            name="newPassword"
                            type="password"
                            placeholder={$t({id: "EnterNewPassword"})}
                            label={$t({id: "ChangePasswordForm.newPasswordLabel"})}
                            required
                            value={formik.values?.newPassword}
                            errors={
                                (!!formik.errors?.newPassword && !!("newPassword" in formik.touched)) || error
                                    ? {
                                          message: formik.errors?.newPassword || error,
                                      }
                                    : null
                            }
                            onChange={(val) => formik.setFieldValue("newPassword", val)}
                            onFocus={() => formik.setFieldTouched("newPassword", false)}
                            onBlur={() => formik.setFieldTouched("newPassword", true)}
                        />
                        <div>{/* <a href="/">Forgot password</a> */}</div>
                    </Forms.ChangePass.Row>
                    <Forms.ChangePass.Row>
                        <FormInput
                            showIcon={false}
                            size="sm"
                            placeholder={$t({id: "ChangePasswordForm.confirmNewPassword"})}
                            label={$t({id: "ChangePasswordForm.confirmNewPasswordLabel"})}
                            name="confirmPassword"
                            type="password"
                            required
                            value={formik.values?.confirmPassword}
                            errors={
                                (!!formik.errors?.confirmPassword && !!formik.touched?.confirmPassword) || error
                                    ? {
                                          message: formik.errors?.confirmPassword || error,
                                      }
                                    : null
                            }
                            onChange={(val) => formik.setFieldValue("confirmPassword", val)}
                            onFocus={() => formik.setFieldTouched("confirmPassword", false)}
                            onBlur={() => formik.setFieldTouched("confirmPassword", true)}
                        />
                    </Forms.ChangePass.Row>
                    <Forms.ChangePass.Row>
                        <Button type="submit" text="Change password" themeKind="blue" size="medium-3" disabled={!formik.isValid || isLoading} />
                    </Forms.ChangePass.Row>
                </Forms.ChangePass.Form>
            </Forms.ChangePass.Wrapper>

            <VerificationPinModal title={$t({id: "ChangePasswordForm.emailConfirmation"})} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <PinForm
                    onRequestCode={sendVerifyCode}
                    modifyButtonText={$t({id: "ModifyPassword"})}
                    onModify={() => {
                        setIsModalOpen(false);
                    }}
                    onSubmit={(v) => submitVerifyCode(v?.pin)}
                    type="pin"
                    {...config}
                />
            </VerificationPinModal>
        </>
    );
};

ChangePasswordForm.displayName = "ChangePasswordForm";
